import { useEffect, useState } from "react";
import AdminLayout from "@/components/admin/AdminLayout";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Building2, CheckCircle2, XCircle, UserPlus, Search, Loader2, Trash2 } from "lucide-react";

const BADGES = {
  approved: { text: "Approvato", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  pending: { text: "In attesa", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  blocked: { text: "Bloccato", cls: "bg-red-50 text-red-700 border-red-200" },
};

export default function AdminSuppliers() {
  const { getToken, user } = useAuth();
  const isSuper = user?.role === "superadmin";
  const API = process.env.REACT_APP_BACKEND_URL;
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ email: "", full_name: "", phone: "", company: "", vat_number: "", password: "", status: "approved", notes: "" });
  const [saving, setSaving] = useState(false);

  const authHeaders = () => ({ Authorization: `Bearer ${getToken()}` });

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.set("supplier_status", statusFilter);
      const res = await fetch(`${API}/api/admin/suppliers?${params.toString()}`, { headers: authHeaders() });
      const data = await res.json();
      setItems(Array.isArray(data) ? data : []);
    } catch {
      toast.error("Errore nel caricamento fornitori");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [statusFilter]); // eslint-disable-line

  const setStatus = async (id, status) => {
    try {
      const res = await fetch(`${API}/api/admin/suppliers/${id}`, {
        method: "PATCH", headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({ status }),
      });
      if (!res.ok) throw new Error();
      toast.success(status === "approved" ? "Fornitore approvato" : status === "blocked" ? "Fornitore bloccato" : "Aggiornato");
      load();
    } catch { toast.error("Errore aggiornamento"); }
  };

  const remove = async (id) => {
    if (!isSuper) return;
    if (!window.confirm("Eliminare definitivamente il fornitore?")) return;
    try {
      const res = await fetch(`${API}/api/admin/suppliers/${id}`, { method: "DELETE", headers: authHeaders() });
      if (!res.ok) throw new Error();
      toast.success("Eliminato");
      load();
    } catch { toast.error("Errore eliminazione"); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/admin/suppliers`, {
        method: "POST", headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.detail || "Errore");
      }
      toast.success("Fornitore creato");
      setShowCreate(false);
      setForm({ email: "", full_name: "", phone: "", company: "", vat_number: "", password: "", status: "approved", notes: "" });
      load();
    } catch (err) { toast.error(err.message || "Errore"); } finally { setSaving(false); }
  };

  const filtered = items.filter((s) => {
    if (!q.trim()) return true;
    const t = q.toLowerCase();
    return [s.email, s.name, s.supplier_company, s.supplier_vat].some((v) => (v || "").toLowerCase().includes(t));
  });

  return (
    <AdminLayout>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display text-2xl font-semibold">Fornitori & Partner</h1>
          <p className="text-sm text-xd-muted mt-1">Gestisci gli account con sconto dedicato. In attesa: {items.filter((i) => i.supplier_status === "pending").length}</p>
        </div>
        <button data-testid="supplier-new-btn" onClick={() => setShowCreate(true)} className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-xd-orange text-white text-sm font-semibold hover:opacity-90">
          <UserPlus className="w-4 h-4" /> Nuovo fornitore
        </button>
      </div>

      <div className="flex gap-3 mb-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-xd-muted" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca ragione sociale, email, P.IVA" className="w-full h-10 pl-9 pr-3 rounded-lg border border-xd-border text-sm focus:outline-none focus:border-xd-cyan" data-testid="supplier-search" />
        </div>
        <select data-testid="supplier-status-filter" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="h-10 px-3 rounded-lg border border-xd-border text-sm">
          <option value="">Tutti gli stati</option>
          <option value="pending">In attesa</option>
          <option value="approved">Approvati</option>
          <option value="blocked">Bloccati</option>
        </select>
      </div>

      {loading ? (
        <div className="py-14 text-center text-xd-muted"><Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Caricamento…</div>
      ) : filtered.length === 0 ? (
        <div className="py-14 text-center border border-dashed border-xd-border rounded-xl">
          <Building2 className="w-8 h-8 mx-auto text-xd-muted" />
          <div className="mt-3 text-sm text-xd-muted">Nessun fornitore trovato</div>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-xd-border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-xd-light/60 text-[11px] font-mono-tech uppercase tracking-widest text-xd-muted">
              <tr>
                <th className="px-4 py-3 text-left">Azienda / Referente</th>
                <th className="px-4 py-3 text-left">P.IVA</th>
                <th className="px-4 py-3 text-left">Contatto</th>
                <th className="px-4 py-3 text-left">Stato</th>
                <th className="px-4 py-3 text-right">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((s) => {
                const badge = BADGES[s.supplier_status] || BADGES.pending;
                return (
                  <tr key={s.id} data-testid={`supplier-row-${s.id}`} className="border-t border-xd-border">
                    <td className="px-4 py-3">
                      <div className="font-medium">{s.supplier_company || "—"}</div>
                      <div className="text-xs text-xd-muted">{s.name}</div>
                    </td>
                    <td className="px-4 py-3 font-mono-tech text-xs">{s.supplier_vat || "—"}</td>
                    <td className="px-4 py-3">
                      <div>{s.email}</div>
                      <div className="text-xs text-xd-muted">{s.phone || "—"}</div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium border ${badge.cls}`}>{badge.text}</span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-1.5 justify-end">
                        {s.supplier_status !== "approved" && (
                          <button data-testid={`supplier-approve-${s.id}`} onClick={() => setStatus(s.id, "approved")} className="inline-flex items-center gap-1.5 h-10 px-3 text-sm rounded-lg bg-emerald-50 text-emerald-700 hover:bg-emerald-100 border border-emerald-200 whitespace-nowrap" title="Approva">
                            <CheckCircle2 className="w-4 h-4" /> Approva
                          </button>
                        )}
                        {s.supplier_status !== "blocked" && (
                          <button data-testid={`supplier-block-${s.id}`} onClick={() => setStatus(s.id, "blocked")} className="inline-flex items-center gap-1.5 h-10 px-3 text-sm rounded-lg bg-red-50 text-red-700 hover:bg-red-100 border border-red-200 whitespace-nowrap" title="Blocca">
                            <XCircle className="w-4 h-4" /> Blocca
                          </button>
                        )}
                        {isSuper && (
                          <button data-testid={`supplier-delete-${s.id}`} onClick={() => remove(s.id)} className="inline-flex items-center h-10 w-10 justify-center rounded-lg text-red-500 bg-red-50 hover:bg-red-100 border border-red-200" title="Elimina">
                            <Trash2 className="w-5 h-5" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {showCreate && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowCreate(false)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="bg-white rounded-xl max-w-lg w-full p-6 space-y-3" data-testid="supplier-create-modal">
            <h3 className="font-display text-xl font-semibold">Crea fornitore</h3>
            {["email","full_name","phone","company","vat_number","password"].map((k) => (
              <input key={k} required type={k === "password" ? "password" : k === "email" ? "email" : "text"} placeholder={({email:"Email",full_name:"Referente",phone:"Telefono",company:"Ragione sociale",vat_number:"P.IVA",password:"Password (min. 8)"})[k]} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="w-full h-10 px-3 rounded-lg border border-xd-border text-sm" data-testid={`supplier-form-${k}`} />
            ))}
            <textarea rows={2} placeholder="Note interne (opzionale)" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-xd-border text-sm" />
            <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="w-full h-10 px-3 rounded-lg border border-xd-border text-sm">
              <option value="approved">Approvato subito</option>
              <option value="pending">In attesa</option>
            </select>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={() => setShowCreate(false)} className="h-10 px-4 rounded-lg border border-xd-border text-sm">Annulla</button>
              <button type="submit" disabled={saving} data-testid="supplier-form-submit" className="h-10 px-4 rounded-lg bg-xd-orange text-white text-sm font-semibold disabled:opacity-60">
                {saving ? "Creazione…" : "Crea fornitore"}
              </button>
            </div>
          </form>
        </div>
      )}
    </AdminLayout>
  );
}
